import React, { useRef, useState } from "react";
import { changeDpiDataUrl } from "changedpi";

function App() {
  const [dpi, setDpi] = useState(72);
  const [resolution, setResolution] = useState({ width: 0, height: 0 });
  const [targetSizeKB, setTargetSizeKB] = useState(100);
  const [format, setFormat] = useState("jpeg");
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.src = ev.target.result;
      img.onload = () => {
        imgRef.current = img;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        setResolution({ width: img.width, height: img.height });
        ctx.drawImage(img, 0, 0);
      };
    };
    reader.readAsDataURL(file);
  };

  // Change resolution
  const changeResolution = () => {
    if (!imgRef.current) return;
    const { width, height } = resolution;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext("2d");
    tempCtx.drawImage(imgRef.current, 0, 0, width, height);

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(tempCanvas, 0, 0);

    imgRef.current = tempCanvas;
  };

  // Change DPI
  const changeDpi = () => {
    if (!imgRef.current) return;

    const canvas = canvasRef.current;
    let dataUrl = canvas.toDataURL(`image/${format}`, 0.95);
    const finalDataUrl = changeDpiDataUrl(dataUrl, dpi);

    const img = new Image();
    img.src = finalDataUrl;
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      imgRef.current = img;
    };
  };

  // Change target size (JPEG only)
  const changeTargetSize = () => {
    if (!imgRef.current || format !== "jpeg") return;

    const canvas = canvasRef.current;
    let dataUrl = canvas.toDataURL("image/jpeg", 0.95);

    let quality = 0.95;
    const step = 0.05;
    let fileSizeKB = Math.round((dataUrl.length * 3) / 4 / 1024);

    while (fileSizeKB > targetSizeKB && quality > 0) {
      quality -= step;
      dataUrl = canvas.toDataURL("image/jpeg", quality);
      fileSizeKB = Math.round((dataUrl.length * 3) / 4 / 1024);
    }

    const img = new Image();
    img.src = dataUrl;
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      imgRef.current = img;
    };
  };

  // Download image
  const downloadImage = () => {
    if (!imgRef.current) return;

    const canvas = canvasRef.current;
    let dataUrl = canvas.toDataURL(`image/${format}`, 0.95);

    if (format === "jpeg" && targetSizeKB > 0) {
      let quality = 0.95;
      const step = 0.05;
      let fileSizeKB = Math.round((dataUrl.length * 3) / 4 / 1024);

      while (fileSizeKB > targetSizeKB && quality > 0) {
        quality -= step;
        dataUrl = canvas.toDataURL("image/jpeg", quality);
        fileSizeKB = Math.round((dataUrl.length * 3) / 4 / 1024);
      }
    }

    const finalDataUrl = changeDpiDataUrl(dataUrl, dpi);

    const link = document.createElement("a");
    link.href = finalDataUrl;
    link.download = `image-${dpi}dpi-${format}${
      format === "jpeg" ? `-${targetSizeKB}KB` : ""
    }.${format}`;
    link.click();
  };

  return (
    <div style={{ textAlign: "center", padding: "2rem" }}>
      <h1>Web Image Editor</h1>
      <input type="file" accept="image/*" onChange={handleImageUpload} />

      <div style={{ marginTop: "1rem" }}>
        <canvas
          ref={canvasRef}
          id="canvas"
          style={{ maxWidth: "100%", border: "1px solid #ccc" }}
        />
      </div>

      <div style={{ marginTop: "1rem" }}>
        {/* Resolution */}
        <div style={{ marginBottom: "1rem" }}>
          <label>Resolution (px): </label>
          <input
            type="number"
            value={resolution.width}
            onChange={(e) =>
              setResolution({ ...resolution, width: Number(e.target.value) })
            }
            style={{ width: "6rem", marginRight: "0.5rem" }}
            disabled={!imgRef.current}
          />
          ×
          <input
            type="number"
            value={resolution.height}
            onChange={(e) =>
              setResolution({ ...resolution, height: Number(e.target.value) })
            }
            style={{ width: "6rem", marginLeft: "0.5rem" }}
            disabled={!imgRef.current}
          />
          <button
            onClick={changeResolution}
            style={{ marginLeft: "1rem", padding: "0.5rem 1rem" }}
            disabled={!imgRef.current}
          >
            Apply Resolution
          </button>
        </div>

        {/* DPI */}
        <div style={{ marginBottom: "1rem" }}>
          <label>DPI: </label>
          <input
            type="number"
            value={dpi}
            onChange={(e) => setDpi(Number(e.target.value))}
            style={{ width: "5rem", marginLeft: "0.5rem" }}
          />
          <button
            onClick={changeDpi}
            style={{ marginLeft: "1rem", padding: "0.5rem 1rem" }}
            disabled={!imgRef.current}
          >
            Apply DPI
          </button>
        </div>

        {/* Format */}
        <div style={{ marginBottom: "1rem" }}>
          <label>Format: </label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            style={{ marginLeft: "0.5rem" }}
          >
            <option value="jpeg">JPEG</option>
            <option value="png">PNG</option>
          </select>
        </div>

        {/* Target size (JPEG only) */}
        {format === "jpeg" && (
          <div style={{ marginBottom: "1rem" }}>
            <label>Target File Size (KB): </label>
            <input
              type="number"
              value={targetSizeKB}
              onChange={(e) => setTargetSizeKB(Number(e.target.value))}
              style={{ width: "6rem", marginLeft: "0.5rem" }}
              disabled={!imgRef.current}
            />
            <button
              onClick={changeTargetSize}
              style={{ marginLeft: "1rem", padding: "0.5rem 1rem" }}
              disabled={!imgRef.current}
            >
              Apply Target Size
            </button>
          </div>
        )}

        {/* Download */}
        <button
          onClick={downloadImage}
          style={{ padding: "0.5rem 1rem" }}
          disabled={!imgRef.current}
        >
          Download Image
        </button>
      </div>
    </div>
  );
}

export default App;
